import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { pdvService } from '../services/pdvService';
import type {
  AdicionarItemDto,
  AdicionarPagamentoDto,
  AplicarDescontoDto,
} from '../types/pdv.types';

// ── Query keys ────────────────────────────────────────────────────────────

export const pdvKeys = {
  venda:         (id: number)   => ['pdv-venda', id] as const,
  buscaProduto:  (q: string)    => ['pdv-produto', q] as const,
  buscaCliente:  (q: string)    => ['pdv-cliente', q] as const,
};

// ── useVendaAtiva ─────────────────────────────────────────────────────────

export const useVendaAtiva = (vendaId: number | null) =>
  useQuery({
    queryKey: pdvKeys.venda(vendaId ?? 0),
    queryFn:  () => pdvService.obterVenda(vendaId!),
    enabled:  vendaId != null && vendaId > 0,
  });

// ── useBuscarProduto ──────────────────────────────────────────────────────

export const useBuscarProduto = (termo: string) =>
  useQuery({
    queryKey: pdvKeys.buscaProduto(termo),
    queryFn:  () => pdvService.buscarProduto(termo),
    enabled:  termo.length >= 2,
    staleTime: 30_000,
  });

// ── useBuscarCliente ──────────────────────────────────────────────────────

export const useBuscarCliente = (termo: string) =>
  useQuery({
    queryKey: pdvKeys.buscaCliente(termo),
    queryFn:  () => pdvService.buscarCliente(termo),
    enabled:  termo.length >= 3,
    staleTime: 60_000,
  });

// ── usePdvOperacoes — mutations de venda ─────────────────────────────────

export const usePdvOperacoes = (vendaId: number | null) => {
  const qc = useQueryClient();

  const invalidar = useCallback(() => {
    if (vendaId) qc.invalidateQueries({ queryKey: pdvKeys.venda(vendaId) });
  }, [qc, vendaId]);

  const abrirVenda = useMutation({
    mutationFn: pdvService.abrirVenda,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const adicionarItem = useMutation({
    mutationFn: ({ dto }: { dto: AdicionarItemDto }) =>
      pdvService.adicionarItem(vendaId!, dto),
    onSuccess: invalidar,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const alterarQuantidade = useMutation({
    mutationFn: ({ itemId, quantidade }: { itemId: number; quantidade: number }) =>
      pdvService.alterarQuantidade(vendaId!, itemId, { quantidade }),
    onSuccess: invalidar,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const removerItem = useMutation({
    mutationFn: (itemId: number) => pdvService.removerItem(vendaId!, itemId),
    onSuccess: invalidar,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const aplicarDesconto = useMutation({
    mutationFn: (dto: AplicarDescontoDto) => pdvService.aplicarDesconto(vendaId!, dto),
    onSuccess: invalidar,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const adicionarPagamento = useMutation({
    mutationFn: (dto: AdicionarPagamentoDto) => pdvService.adicionarPagamento(vendaId!, dto),
    onSuccess: invalidar,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const removerPagamento = useMutation({
    mutationFn: (pagId: number) => pdvService.removerPagamento(vendaId!, pagId),
    onSuccess: invalidar,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const finalizar = useMutation({
    mutationFn: () => pdvService.finalizar(vendaId!),
    onSuccess: () => {
      invalidar();
      toast.success('Venda finalizada com sucesso!');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelar = useMutation({
    mutationFn: () => pdvService.cancelar(vendaId!),
    onSuccess: () => {
      invalidar();
      toast.info('Venda cancelada.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    abrirVenda,
    adicionarItem,
    alterarQuantidade,
    removerItem,
    aplicarDesconto,
    adicionarPagamento,
    removerPagamento,
    finalizar,
    cancelar,
  };
};

// ── usePdvState — estado local da sessão PDV ──────────────────────────────

export const usePdvState = () => {
  const [vendaId,        setVendaId]        = useState<number | null>(null);
  const [buscaTermo,     setBuscaTermo]     = useState('');
  const [clienteTermo,   setClienteTermo]   = useState('');
  const [termoBuscaAtivo, setTermoBuscaAtivo] = useState('');

  const iniciarNovaSessao = useCallback((id: number) => {
    setVendaId(id);
    setBuscaTermo('');
    setClienteTermo('');
  }, []);

  const encerrarSessao = useCallback(() => {
    setVendaId(null);
    setBuscaTermo('');
    setClienteTermo('');
    setTermoBuscaAtivo('');
  }, []);

  return {
    vendaId,
    buscaTermo,
    setBuscaTermo,
    clienteTermo,
    setClienteTermo,
    termoBuscaAtivo,
    setTermoBuscaAtivo,
    iniciarNovaSessao,
    encerrarSessao,
  };
};
